import { DatePicker } from "@app/components/DatePicker.tsx";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { Box } from "@mui/material";
import { IconButton } from "bi-lib";
import { Moment } from "moment";
import moment from "moment/moment";
import { useMemo } from "react";

type acceptableTypes = string | Moment | Date;

interface Props<T> {
    label: string;
    value?: T;
    onChange: (value: T) => void;
    gap?: number;
    disabled?: boolean;
}

/**
 * A DateInput component that allows users to select, navigate, and manipulate date values.
 * Provides functionality to handle date restrictions (e.g., minimum and maximum dates) and
 * supports customization such as gap size and disabled state.
 *
 * @param {object} options - The options object containing props for the DateInput component.
 * @param {string} options.label - The label for the DatePicker input field.
 * @param {T} options.value - The currently selected date value. Can be of type `acceptableTypes`.
 * @param {function(T): void} options.onChange - Callback function invoked when the date value changes.
 * @param {number} [options.gap=1] - The spacing between the elements in the component layout.
 * @param {boolean} [options.disabled=false] - Whether the component is in a disabled state.
 * @returns {JSX.Element} The rendered DateInput component.
 */
export default function DateInput<T extends acceptableTypes>({
    label,
    value,
    onChange,
    gap = 1,
    disabled = false,
}: Props<T>) {
    const valueInternal: Moment | undefined = useMemo(() => {
        if (value == undefined) {
            return undefined;
        }
        return typeof value != "string" ? moment(value) : moment(value, "MM/DD/YYYY");
    }, [value]);

    const handleChange = (date: Moment, setter: (value: T) => void) => {
        if (typeof date == typeof value) {
            setter(date as T);
            return;
        }

        if (typeof value == "string") {
            const newValue = date.format("MM/DD/YYYY");
            setter(newValue as T);
            return;
        }

        if (value instanceof Date) {
            const newValue = date.toDate();
            setter(newValue as T);
        }

        setter(date.format("MM/DD/YYYY") as T);
    };

    const handleSet = (e: Moment | null) => {
        if (!e?.isValid() || e?.year() < 1000) {
            return;
        }
        handleChange(e, onChange);
    };

    const handlePrevious = () => {
        if (valueInternal == undefined) {
            return;
        }
        const date = valueInternal.clone();
        date.subtract(1, "day");
        handleChange(date, onChange);
    };

    const handleNext = () => {
        if (valueInternal == undefined) {
            return;
        }
        const date = valueInternal.clone();
        date.add(1, "day");
        handleChange(date, onChange);
    };

    return (
        <Box display={"flex"} alignItems={"center"} gap={gap}>
            <IconButton tooltip={"Set to Today"} onClick={() => handleSet(moment())} size={"small"} disabled={disabled}>
                <EventRepeatIcon />
            </IconButton>
            <IconButton
                tooltip={"Previous Day"}
                onClick={handlePrevious}
                disabled={disabled || valueInternal == undefined}
                size={"small"}
            >
                <ChevronLeftIcon />
            </IconButton>
            <DatePicker
                label={label}
                value={valueInternal}
                onChange={handleSet}
                sx={{ flexGrow: 1, minWidth: 150 }}
                disabled={disabled}
            />
            <IconButton
                tooltip={"Next Day"}
                onClick={handleNext}
                disabled={disabled || valueInternal == undefined}
                size={"small"}
            >
                <ChevronRightIcon />
            </IconButton>
        </Box>
    );
}
